<template>
  <ul
    ref="listbox"
    role="listbox"
    :class="['listbox', { open, disabled }]"
    :style="{ width: `${calculatedWidth}px` }"
  >
    <li
      ref="listbox-label"
      class="listbox-label"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :aria-label="label"
      @click="toggleListBox"
      @keydown.enter="toggleListBox"
      @keydown.space="toggleListBox"
      @keydown.up="focusNextOption('up')"
      @keydown.down="focusNextOption('down')"
      @keydown.escape="closeListBox"
    >
      {{ listboxLabel }}
      <icon
        name="arrow-down"
        class="listbox-arrow"
        variant="d3d3d3"
      />
    </li>
    <li
      ref="listbox-options-container"
      class="options-container"
      :aria-expanded="open ? true : false"
    >
      <ul class="options-list">
        <li
          v-for="(option, index) in Object.keys(keyedOptions)"
          :key="keyedOptions[option].value"
          ref="listbox-options"
          class="listbox-option"
          role="button"
          tabindex="0"
          :value="keyedOptions[option].value"
          @click="handleOptionSelect(option)"
          @keydown.enter="handleOptionSelect(option)"
          @keydown.space="handleOptionSelect(option)"
          @keydown.up="focusNextOption('up', index)"
          @keydown.down="focusNextOption('down', index)"
          @keydown.escape="closeListBox"
        >
          {{ keyedOptions[option].text }}
        </li>
      </ul>
    </li>
  </ul>
</template>

<script>
import Icon from '@components/singletons/icon';

export default {
  components: { Icon },
  props: {
    disabled: {
      type: Boolean,
    },
    label: {
      type: String,
    },
    options: {
      type: Array,
    },
    type: {
      type: String,
    },
    value: {
      type: [String, Number],
    },
  },
  emits: ['change'],
  data() {
    return {
      open: false,
    };
  },
  computed: {
    calculatedWidth() {
      // This is suuuuuuupppper hacky and not great but #yolo
      let options = [this.label, ...this.options.map(opt => opt.text ? opt.text : opt)];
      options.sort((a, b) => {
        // The use of `` is to ensure the value is a string so the .length property is not undefined
        return `${a}`.length < `${b}`.length ? 1 : -1;
      });

      // Completely baseless, random guess here
      const characterPxWidth = 10;

      // 20 is the padding of the listbox
      // 10 is the width of the arrow
      // 10 is the space we want to keep from the longest option and the arrow
      return (characterPxWidth * `${options[0]}`.length) + 40;
    },
    keyedOptions() {
    // Will return an object that is keyed based on value
      return this.options.reduce((prevValue, option) => {
        const optionValue = option.value ? option.value : option;
        const optionText = option.text ? option.text : option;

        return {
          ...prevValue,
          [optionValue]: {
            value: optionValue,
            text: optionText,
          },
        };
      }, {});
    },
    listboxLabel() {
      if (this.value) {
        const isOption = this.keyedOptions[this.value];
        return isOption ? this.keyedOptions[this.value].text : this.value;
      }

      return this.label;
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    closeListBox() {
      this.open = false;
      this.$refs['listbox-label'].focus();
      document.removeEventListener('click', this.handleClickOutside);
    },
    focusNextOption(direction, currentIndex) {
      // We don't pass the current index on the label so we can use that as a indicator here
      let nextItem;
      if (!currentIndex) {
        if (direction === 'down') {
          nextItem = this.$refs['listbox-options'][0];
        } else {
          nextItem = this.$refs['listbox-options'][this.options.length - 1];
        }
      }

      if (direction === 'down') {
        // Handling for if we are at the end of the list
        if (currentIndex + 1 === this.options.length) {
          nextItem = this.$refs['listbox-options'][0];
        } else {
          nextItem = this.$refs['listbox-options'][currentIndex + 1];
        }
      } else {
        // Handling for if we are at the start of the list
        if (currentIndex === 0) {
          nextItem = this.$refs['listbox-options'][this.options.length - 1];
        }
        nextItem = this.$refs['listbox-options'][currentIndex - 1];
      }

      if (nextItem) {
        nextItem.focus();
      }
    },
    handleClickOutside(event) {
      const listbox = this.$refs.listbox;
      const targetElement = event.target;
      if (listbox !== targetElement && !listbox.contains(targetElement)) {
        this.closeListBox();
      }
    },
    handleOptionSelect(value) {
      this.$emit('change', value, this.type);
      this.open = false;
      this.$refs['listbox-label'].focus();
    },
    toggleListBox() {
      if (this.open) {
        this.closeListBox();
      } else {
        this.open = true;
        document.addEventListener('click', this.handleClickOutside);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .listbox {
    display: inline-block;
    background-color: #fff;
    border: 1px solid #d3d3d3;
    list-style: none;
    margin: 0;
    padding: 0;
    border-radius: 5px;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;
    text-align: left;

    &.disabled {
      color: #989898;
      pointer-events: none;
    }

    .listbox-label {
      border: 1px solid transparent;
      padding: rem-calc(10);
      border-radius: 5px;

      .listbox-arrow {
        height: rem-calc(8);
        position: absolute;
        top: 14px;
        right: 10px;
        transform: initial;
        transition: .2s all ease;
      }
    }

    .options-container {
      position: relative;
      display: none;
      width: 100%;
      z-index: 5;

      .options-list {
        position: absolute;
        // This is offset the border of the container
        left: -1px;
        padding: 0;
        margin: 0;
        list-style-type: none;
        background-color: #fff;
        width: 100%;
        overflow-y: auto;
        border: 1px solid #000;
        border-radius: 0 0 5px 5px;
        border-top-color: transparent;
        border-top-width: 0px;

        // Show 8 items max
        // Height of text (16) + padding (20) * 8
        max-height: rem-calc(288);

        // CSS for hiding scroll bar in listbox
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none;  /* Internet Explorer 10+ */

        &::-webkit-scrollbar { /* WebKit */
          width: 0;
          height: 0;
        }
      }

      .listbox-option {
        padding: rem-calc(10) rem-calc(10) rem-calc(10) rem-calc(15);
        border-bottom: 1px solid #edf0f5;

        &:last-of-type {
          border-color: transparent;
        }

        &:hover {
          background-color: #edf0f5;
        }
      }
    }

    &.open {
      border-color: #000;
      border-radius: 5px 5px 0 0;

      .listbox-label {
        border-color: #edf0f5;

        .listbox-arrow {
          transform: rotate(180deg);
        }
      }

      .options-container {
        display: block;
      }
    }
  }
</style>
